import React from "react";
import { Link } from "react-router-dom";
import PermissionsGate from "../../../../utils/permissionGate";
import { TableLink } from "../../../library/DataTable";
import IconButton, { StyledActionGroup, StyledIconLink } from "../../../library/IconButton";
import { StyledCampusBadge } from "./linkedProgrammes.styles";
import {
    StyledGradeAction,
    StyledGradeBadge,
    StyledGradeCellContent,
    StyledGradeMeta,
    StyledGradeStatus,
    StyledMethod,
    StyledModulesButton,
    StyledProgrammeDetails,
    StyledProgrammeMetaRow,
    StyledReadinessCount,
    StyledReadinessRow,
    StyledReadinessState,
    StyledReadinessStateIcon,
    StyledReadinessText,
    StyledReadinessWarning,
    StyledReadinessWarnings,
    StyledVersionActiveIcon,
    StyledVersionInactiveIcon,
    StyledVersionValue,
} from "./LinkedProgrammesTable.styles";
import {
    getCampusText,
    getGradeMeta,
    getMethodIcon,
    getMethodText,
    getReadiness,
    isVersionActive,
} from "../../../../utils/LinkedProgrammeUtils";

/**
 * "Programme Details" cell: name (linked if it has an id), campus and delivery method.
 * @param {object} row Linked-programme row.
 */
export function ProgrammeDetailsCell({ row }) {
    const campusText = getCampusText(row?.campus);
    const methodText = getMethodText(row?.type);

    return (
        <StyledProgrammeDetails>
            {row?.id ? (
                <PermissionsGate
                    scopes={["matpedit"]}
                    RenderError={() => row?.name || "-"}
                >
                    <TableLink
                        as={Link}
                        to={`/courseAdministration/Programmes/programme/open/${row.id}/details`}
                        target="_self"
                        title={row?.name}
                    >
                        {row?.name || "-"}
                    </TableLink>
                </PermissionsGate>
            ) : (
                row?.name || "-"
            )}

            <StyledProgrammeMetaRow>
                <StyledCampusBadge $campus={campusText}>
                    {campusText}
                </StyledCampusBadge>

                <StyledMethod>
                    <i
                        className={`fal fa-${getMethodIcon(methodText)}`}
                        aria-hidden="true"
                    />
                    {methodText}
                </StyledMethod>
            </StyledProgrammeMetaRow>
        </StyledProgrammeDetails>
    );
}

/**
 * "Version" cell: active/inactive icon plus the version name, linked to that version's details.
 * @param {object} row Linked-programme row.
 * @param {string|number} programmeId PMC programme id.
 */
export function VersionCell({ row, programmeId }) {
    if (!row?.version_name) {
        return "-";
    }

    return (
        <StyledVersionValue>
            {isVersionActive(row) ? (
                <StyledVersionActiveIcon
                    className="fas fa-check-circle"
                    aria-hidden="true"
                />
            ) : (
                <StyledVersionInactiveIcon
                    className="fas fa-times-circle"
                    aria-hidden="true"
                />
            )}
            <TableLink
                as={Link}
                to={`/program-management-center/programmes/versions/details?versionId=${row.active_version_id}&programmeId=${programmeId}`}
                title={row.version_name}
            >
                {row.version_name}
            </TableLink>
        </StyledVersionValue>
    );
}

/**
 * "Grades" cell: lock state badge plus the lock/unlock toggle button.
 * @param {object} row Linked-programme row.
 * @param {object} hook State/handlers from the linked-programmes list hook.
 */
export function GradeCell({ row, hook }) {
    const locked = Number(row?.isGradeLocked) === 1; // 1 = grades locked
    const gradeMeta = getGradeMeta(row);
    const isRowLocking = Boolean(hook.gradeLockLoading?.[row.id]);

    return (
        <StyledGradeCellContent>
            <StyledGradeStatus>
                <StyledGradeBadge $locked={locked}>
                    <i
                        className={locked ? "fal fa-lock" : "fal fa-unlock"}
                        aria-hidden="true"
                    />
                    {locked ? "Grades Locked" : "Grades Unlocked"}
                </StyledGradeBadge>

                {gradeMeta && (
                    <StyledGradeMeta title={gradeMeta}>
                        {gradeMeta}
                    </StyledGradeMeta>
                )}
            </StyledGradeStatus>

            <PermissionsGate scopes={["capgsedit"]} errorProps={{ disabled: true }}>
                <StyledGradeAction
                    type="button"
                    $action={isRowLocking ? "loading" : locked ? "unlock" : "lock"}
                    disabled={
                        hook.isProcessing ||
                        isRowLocking ||
                        hook.gradeLockLoading?.lockAll ||
                        hook.gradeLockLoading?.unlockAll
                    }
                    onClick={() => hook.handleLockGrade(row.id, row.isGradeLocked)}
                    aria-label={locked ? "Unlock grades" : "Lock grades"}
                    title={locked ? "Unlock grades" : "Lock grades"}
                >
                    <i
                        className={
                            isRowLocking
                                ? "fas fa-sync fa-spin"
                                : locked
                                    ? "fas fa-unlock"
                                    : "fas fa-lock"
                        }
                        aria-hidden="true"
                    />
                </StyledGradeAction>
            </PermissionsGate>
        </StyledGradeCellContent>
    );
}

/**
 * "Action" cell: open-programme and unlink buttons.
 * @param {object} row Linked-programme row.
 * @param {object} hook State/handlers from the linked-programmes list hook.
 */
export function ActionsCell({ row, hook }) {
    return (
        <StyledActionGroup>
            <PermissionsGate
                scopes={["matpedit"]}
                RenderError={() => (
                    <IconButton
                        icon="folder-open"
                        variant="solid"
                        disabled
                        ariaLabel="Open programme"
                    />
                )}
            >
                <StyledIconLink
                    to={`/courseAdministration/Programmes/programme/open/${row.id}/details`}
                    title="Open programme"
                >
                    <IconButton
                        icon="folder-open"
                        variant="solid"
                        ariaLabel="Open programme"
                    />
                </StyledIconLink>
            </PermissionsGate>

            <PermissionsGate
                scopes={["matpadd"]}
                RenderError={() => (
                    <IconButton
                        icon="unlink"
                        variant="destructive"
                        disabled
                        ariaLabel="Unlink programme"
                    />
                )}
            >
                <IconButton
                    icon="unlink"
                    variant="destructive"
                    disabled={
                        hook.isProcessing ||
                        Boolean(hook.gradeLockLoading?.[row.id]) ||
                        Boolean(hook.gradeLockLoading?.lockAll) ||
                        Boolean(hook.gradeLockLoading?.unlockAll)
                    }
                    onClick={() => hook.startConfirmation("unlink", [row.id], row)}
                    ariaLabel="Unlink programme"
                />
            </PermissionsGate>
        </StyledActionGroup>
    );
}

/**
 * "Module readiness" cell: ready/error summary plus the "Modules" popover toggle.
 * @param {object} row Linked-programme row.
 * @param {object} hook State/handlers from the linked-programmes list hook.
 * @param {object} modulesRow Row the readiness popover is currently open for, if any.
 * @param {Function} onToggleModules Opens/closes the popover for this row (event) => void.
 */
export function ReadinessCell({ row, hook, modulesRow, onToggleModules }) {
    const readiness = getReadiness(row);
    const isOpenForRow = modulesRow?.id === row.id;

    return (
        <StyledReadinessRow>
            <StyledReadinessState>
                <StyledReadinessStateIcon
                    $ready={readiness.problems.length === 0}
                    className={`fas ${
                        readiness.problems.length === 0
                            ? "fa-circle-check"
                            : "fa-times-circle"
                    }`}
                    aria-hidden="true"
                />

                <StyledReadinessText>
                    <StyledReadinessCount>
                        {readiness.readyCount} / {readiness.total} modules ready
                    </StyledReadinessCount>

                    {readiness.warnings.length > 0 && (
                        <StyledReadinessWarnings>
                            {readiness.warnings.map((warning) => (
                                <StyledReadinessWarning key={warning}>
                                    <i
                                        className="fas fa-exclamation-triangle"
                                        aria-hidden="true"
                                    />
                                    {warning}
                                </StyledReadinessWarning>
                            ))}
                        </StyledReadinessWarnings>
                    )}
                </StyledReadinessText>
            </StyledReadinessState>

            <StyledModulesButton
                type="button"
                $active={isOpenForRow}
                disabled={hook.isProcessing}
                aria-expanded={isOpenForRow}
                onClick={(event) => onToggleModules(row, event)}
            >
                Modules
                <i className="fas fa-bars" aria-hidden="true" />
            </StyledModulesButton>
        </StyledReadinessRow>
    );
}
